import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import React, { useEffect, useState } from 'react';
import TableFormWrapper from 'components/atoms/FormWrapper';
import Grid from '@mui/material/Grid';
import { StyledValueFormHeader } from 'pages/SystemSettingsPage/styles';
import { Box } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { useNavigate } from 'react-router-dom';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { Resolver, useForm } from 'react-hook-form';
import { useScroll } from 'hooks/useScroll';
import { ActionTypes } from 'state/actions/alert';
import { useDocumentsGeneratingSetupPageSchema } from 'pages/SystemSettingsPage/DocumentsGeneratingSetupPage/schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDocumentsGeneratingSetup } from 'pages/SystemSettingsPage/DocumentsGeneratingSetupPage/hooks';

export interface IDocumentsGeneratingFormValues {
  eTraceEnabled: string | null;
  iArmsEnabled: string | null;
}

export const DocumentsGeneratingSetupPage = () => {
  const { t } = useTranslations();
  const { samePageAlert, clearAlert, setAlert } = useAlert();
  const navigate = useNavigate();
  const [isSubmitLoading, setSubmitLoading] = useState(false);
  const [initialValues, setInitialValues] = useState<IDocumentsGeneratingFormValues>({
    eTraceEnabled: null,
    iArmsEnabled: null,
  });
  const { updateDocumentsGeneratingData, loadInitData, eTraceEnabled, iArmsEnabled } =
    useDocumentsGeneratingSetup();
  const schema = useDocumentsGeneratingSetupPageSchema();
  const { elRef, scrollToError } = useScroll();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<IDocumentsGeneratingFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(schema) as unknown as Resolver<IDocumentsGeneratingFormValues>,
  });

  useEffect(() => {
    (async () => {
      scrollToError(errors);
    })();
  }, [errors]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    loadInitData();
  }, []);

  useEffect(() => {
    if (eTraceEnabled !== null) {
      const value = eTraceEnabled ? 'yes' : 'no';
      setValue('eTraceEnabled', value);
      setInitialValues((prevState) => ({
        ...prevState,
        eTraceEnabled: value,
      }));
    }
  }, [eTraceEnabled]);

  useEffect(() => {
    if (iArmsEnabled !== null) {
      const value = iArmsEnabled ? 'yes' : 'no';
      setValue('iArmsEnabled', value);
      setInitialValues((prevState) => ({
        ...prevState,
        iArmsEnabled: value,
      }));
    }
  }, [iArmsEnabled]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const formChanged = (
    formInitialValues?: IDocumentsGeneratingFormValues | null,
    formCurrentValues?: IDocumentsGeneratingFormValues | null,
  ) =>
    formInitialValues?.eTraceEnabled !== formCurrentValues?.eTraceEnabled ||
    formInitialValues?.iArmsEnabled !== formCurrentValues?.iArmsEnabled;

  const onSubmitForm = async (data: IDocumentsGeneratingFormValues) => {
    if (!formChanged(initialValues, data)) {
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('general.noChanged.warning.message'),
        variant: EAlertVariants.warning,
      });
      return;
    }
    if (data.iArmsEnabled !== null && data.eTraceEnabled !== null) {
      setSubmitLoading(true);
      await updateDocumentsGeneratingData(data);
      setSubmitLoading(false);
      setInitialValues(data);
      setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('documentsGeneratinPage.success.text'),
      });
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          t('documentsGeneratingSetup.header'),
        ]}
      />
      <StyledPageTitle variant="h4">{t('documentsGeneratingSetup.header')}</StyledPageTitle>
      <TableFormWrapper>
        <form onSubmit={handleSubmit(onSubmitForm)}>
          <Grid container ref={elRef}>
            <Grid sx={{ rowGap: 1 }}>
              <Grid container>
                <Grid sx={{ mb: 3 }} size={{ xs: 12 }}>
                  <StyledValueFormHeader>
                    {t('documentsGeneratingSetup.section.header')}
                  </StyledValueFormHeader>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Box sx={{ mb: 1 }}>
                    <FormRadioGroup
                      name="eTraceEnabled"
                      label={t('documentsGenerating.eTraceRadioButtons.label')}
                      options={[
                        { label: t('general.yes.label'), value: 'yes' },
                        { label: t('general.no.label'), value: 'no' },
                      ]}
                      control={control}
                      errors={errors}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Box sx={{ mb: 1 }}>
                    <FormRadioGroup
                      name="iArmsEnabled"
                      label={t('documentsGenerating.iArmsRadioButtons.label')}
                      options={[
                        { label: t('general.yes.label'), value: 'yes' },
                        { label: t('general.no.label'), value: 'no' },
                      ]}
                      control={control}
                      errors={errors}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 1, mt: 1 }}>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  type="submit"
                  fullWidth
                  label={t('general.save.button')}
                  isLoading={isSubmitLoading}
                  id="save-button"
                  variant={EButtonVariants.contained}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 3 }}>
                <Button
                  fullWidth
                  label={t('general.cancel.button')}
                  id="cancel-button"
                  variant={EButtonVariants.outlined}
                  onClick={() => navigate(ERouteLinks.Administration)}
                />
              </Grid>
            </Grid>
          </Grid>
        </form>
      </TableFormWrapper>
      {samePageAlert && (
        <Box sx={{ mt: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
    </>
  );
};
